import { useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { useAuthenticatedUser, useWorkspace } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import AIAgent from '../../../../src/ai-agent/AIAgent';
import { Analytics } from '../../../../src/analytics/Analytics';
import CompetencySearch, {
	CompetencySearchStart,
} from '../../../../src/competencies/CompetencySearch';
import UserCompetencies from '../../../../src/competencies/UserCompetencies';
import ViewerCompetencies from '../../../../src/competencies/ViewerCompetencies';
import Conversations from '../../../../src/conversations/Conversations';
import DevelopmentPlanRoute from '../../../../src/development-plan/DevelopmentPlanRoute';
import EngagementReplies from '../../../../src/engagement/replies/EngagementReplies';
import Compliment from '../../../../src/feedback/Compliment';
import FeedbackResponseComposer from '../../../../src/feedback/FeedbackResponseComposer';
import OutgoingFeedback from '../../../../src/feedback/OutgoingFeedback';
import SharedFeedback from '../../../../src/feedback/SharedFeedback';
import UserCompliments from '../../../../src/feedback/UserCompliments';
import UserFeedback from '../../../../src/feedback/UserFeedback';
import UserOutgoingFeedback from '../../../../src/feedback/UserOutgoingFeedback';
import ViewerCompliments from '../../../../src/feedback/ViewerCompliments';
import ViewerFeedback from '../../../../src/feedback/ViewerFeedback';
import ViewerOutgoingFeedback from '../../../../src/feedback/ViewerOutgoingFeedback';
import FeedbackResults from '../../../../src/feedback/results/FeedbackRequest';
import Goals from '../../../../src/goals/Goals';
import GrowthPath from '../../../../src/growth-path/pages/GrowthPath';
import Home from '../../../../src/home/Home';
import JobPosition from '../../../../src/job/JobPosition';
import Layout from '../../../../src/layout/Layout';
import Learning from '../../../../src/learning/Learning';
import Manager from '../../../../src/manager/Manager';
import Notes from '../../../../src/notes/Notes';
import HelpdeskContainer from '../../../../src/packages/kakapo-ui-application/common/containers/HelpdeskContainer';
import Redirects from '../../../../src/packages/kakapo-ui-application/containers/Redirects';
import PersonalFiles from '../../../../src/personal-files/PersonalFiles';
import PersonalSettings from '../../../../src/personal-settings/PersonalSettings';
import Recommend from '../../../../src/recommend/Recommend';
import Settings from '../../../../src/settings/Settings';
import TalentReview from '../../../../src/talent-reviews/TalentReview';
import TalentReviewsList from '../../../../src/talent-reviews/TalentReviews';
import TalentReviewV2Routes from '../../../../src/talent-reviews/v2/TalentReviewV2Routes';
import Login from './pages/authentication/Login';
import Logout from './pages/authentication/Logout';
import Journey from './pages/journey/Journey';

function ViewerRedirect() {
	const history = useHistory();

	useEffect(() => {
		const destination = history.location.pathname.replace(/^\/users\/[^/]+/, ``);
		history.replace({ pathname: destination, search: history.location.search });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}

function UserProfileRedirect(props: { userID: string }) {
	const query = graphql.useUserQuery({ variables: { id: props.userID } });

	if (!query.data) {
		return null;
	}

	if (query.data.user.viewerCanViewJourney) {
		return <Redirect to={`/users/${props.userID}/journey`} />;
	}

	return <Redirect to={`/users/${props.userID}/notes`} />;
}

export function MainRoutes() {
	const currentUser = useAuthenticatedUser();

	return (
		<Switch>
			<Route
				path={['/', '/journey', '/journey/jobs', '/journey/learning/:id', '/inbox/manager']}
				exact
			>
				<Home />
			</Route>
			<Route path="/manager">
				<Manager />
			</Route>
			<Route path="/assistant">
				<AIAgent />
			</Route>
			<Route path="/personal-settings">
				<PersonalSettings />
			</Route>
			<Route path="/recommend" exact>
				<Recommend />
			</Route>
			<Route path="/personal-files">
				<PersonalFiles userID={currentUser.id} />
			</Route>
			{/* Legacy cycle manager reminder emails */}
			<Redirect
				path="/analytics/v2/cycles/:cycleTemplateID/moment/:momentID/managers/:managerID"
				to="/analytics/cycles/:cycleTemplateID/moment/:momentID/managers/:managerID"
			/>
			<Redirect
				path="/analytics/v2/cycles/:cycleTemplateID"
				to="/analytics/cycles/:cycleTemplateID"
			/>
			<Route path="/analytics">
				<Analytics />
			</Route>
			<Route path="/engagement-replies/:answerManagerID" exact>
				{({ match }) =>
					!!match && <EngagementReplies answerManagerID={match!.params.answerManagerID} />
				}
			</Route>
			<Route path={`/users/${currentUser.id}`}>
				<ViewerRedirect />
			</Route>
			<Route path={['/goals', '/users/:id/goals']}>
				<Goals />
			</Route>
			<Route path="/users/:id" exact>
				{({ match }) => !!match && <UserProfileRedirect userID={match!.params.id} />}
			</Route>
			<Route path="/users/:id/personal-files">
				{({ match }) => <PersonalFiles userID={match!.params.id} />}
			</Route>
			<Route path="/users/:id/journey">
				{({ match }) => <Journey userID={(match?.params as Record<string, string>)['id']} jumbo />}
			</Route>
			<Route exact path={['/feedback/given/:id', '/users/:userID/feedback/given/:id']}>
				<OutgoingFeedback />
			</Route>
			<Route exact path="/feedback/given">
				<ViewerOutgoingFeedback />
			</Route>
			<Route exact path="/feedback/shared">
				<SharedFeedback />
			</Route>
			<Route exact path="/users/:id/feedback/given">
				<UserOutgoingFeedback />
			</Route>
			<Route exact path={['/feedback/compliments', '/feedback/compliments/new']}>
				<ViewerCompliments />
			</Route>
			<Route
				exact
				path={[
					'/users/:id/feedback/compliments',
					'/users/:id/feedback/compliments/new',
					'/users/:id/feedback/compliments/thank-you',
				]}
			>
				<UserCompliments />
			</Route>
			<Route exact path={['/feedback/compliments/:id', '/users/:userID/feedback/compliments/:id']}>
				<Compliment />
			</Route>
			<Route
				path={[
					'/feedback',
					'/feedback/send',
					'/feedback/request',
					'/feedback/request-from-moment',
					'/feedback/request-from-moment/:id',
					'/feedback/request-from-suggestion',
					'/feedback/request/competencies',
				]}
				exact
			>
				<ViewerFeedback />
			</Route>
			<Route path={['/feedback/:requestID/respond', '/users/:id/feedback/:requestID/respond']}>
				{({ match }) => <FeedbackResponseComposer requestID={match!.params.requestID} />}
			</Route>
			<Route
				path={[
					'/users/:id/feedback',
					'/users/:id/feedback/send',
					'/users/:id/feedback/request',
					'/users/:id/feedback/request-from-moment',
					'/users/:id/feedback/request-from-moment/:momentID',
					'/users/:id/feedback/request-from-suggestion',
					'/users/:id/feedback/request/competencies',
				]}
				exact
			>
				<UserFeedback />
			</Route>
			<Route path={['/feedback/:requestID', '/users/:id/feedback/:requestID']}>
				{({ match }) => <FeedbackResults id={match!.params.requestID} />}
			</Route>
			<Route path="/competencies/search" exact>
				<CompetencySearchStart />
			</Route>
			<Route path="/competencies/search/:id">
				<CompetencySearch />
			</Route>
			<Route path="/competencies/:jobTitleID">
				<ViewerCompetencies />
			</Route>
			<Redirect path="/competencies" to="/competencies/all" />
			<Route path="/job-positions/:id">
				<JobPosition />
			</Route>
			<Route path={['/users/:id/competencies/:jobTitleID', '/users/:id/competencies/all']}>
				<UserCompetencies />
			</Route>
			<Redirect path="/v2/users/:id/competencies" to="/v2/users/:id/competencies/all" />
			<Route path="/growth-path/search" exact>
				<CompetencySearchStart />
			</Route>
			<Route path="/growth-path/search/:id">
				<CompetencySearch />
			</Route>
			<Route
				path={[
					'/users/:userID/growth-path/my-job-title/:jobTitleID/competency/:competencyID',
					'/users/:userID/growth-path/my-job-title/:jobTitleID',
					'/users/:userID/growth-path/career/:careerID/job-title/:jobTitleID/competency/:competencyID',
					'/users/:userID/growth-path/career/:careerID/job-title/:jobTitleID',
					'/users/:userID/growth-path/career/:careerID',
					'/users/:userID/growth-path/job-title/:jobTitleID/competency/:competencyID',
					'/users/:userID/growth-path/job-title/:jobTitleID',
					'/growth-path/my-job-title/:jobTitleID/competency/:competencyID',
					'/growth-path/my-job-title/:jobTitleID',
					'/growth-path/career/:careerID/job-title/:jobTitleID/competency/:competencyID',
					'/growth-path/career/:careerID/job-title/:jobTitleID',
					'/growth-path/career/:careerID',
					'/growth-path/job-title/:jobTitleID/competency/:competencyID',
					'/growth-path/job-title/:jobTitleID',
				]}
			>
				<GrowthPath />
			</Route>
			<Redirect exact path="/growth-path" to="/growth-path/my-job-title/all" />
			<Redirect path="/users/:userID/grow-path" to="/users/:userID/growth-path/my-job-title/all" />

			<Route path={['/conversations', '/users/:id/conversations']}>
				<Conversations />
			</Route>
			<Route path={['/notes', '/users/:id/notes']}>
				<Notes />
			</Route>
			<Route path={['/development-plan']}>
				<DevelopmentPlanRoute />
			</Route>
			<Route path={['/learning', '/users/:id/learning']}>
				<Learning />
			</Route>
			<Route path="/settings">
				<Settings />
			</Route>
			<Route path="/talent-reviews/v2">
				<TalentReviewV2Routes />
			</Route>
			<Route exact path={['/talent-reviews', '/talent-reviews/(department|team)/:id/new']}>
				<TalentReviewsList />
			</Route>
			{/* The department and team routes are used in the email flow, and are to be removed in the future. */}
			<Route
				path={[
					'/talent-reviews/:id',
					'/department/:department/talent-reviews/:id',
					'/team/:team/talent-reviews/:id',
				]}
			>
				{({ match }) => <TalentReview id={match!.params.id} />}
			</Route>
			{/* The meeting routes are used in e-mail flows and can also be used for calender UI in the future. */}
			<Route path="/meetings/:targetTypeID">
				{({ match }) => {
					const targetTypeID = match!.params.targetTypeID;
					const targetType = targetTypeID.split('-')[0];
					const targetID = targetTypeID.split('-').slice(1).join('-');

					if (targetType === 'note') {
						return <Redirect to={`/notes/${targetID}`} />;
					}

					return <Redirect to={`/`} />;
				}}
			</Route>
			<Route path="/logout">
				<Logout />
			</Route>
			<Redirects />
		</Switch>
	);
}

function LaunchRedirect() {
	const workspace = useWorkspace();

	if (workspace.id !== 'launch') {
		return null;
	}

	return (
		<Switch>
			<Route path="/settings/workspaces" />
			<Route path="/settings/workbench" />
			<Route path="/settings/modules" />
			<Route path="/settings/announcements" />
			<Route path="/settings/analytics" />
			<Route>
				<Redirect to="/settings/workspaces" />
			</Route>
		</Switch>
	);
}

export default function MainRouter() {
	return (
		<HelpdeskContainer>
			<Switch>
				<Route path="/authentication">
					<Login />
				</Route>
				<Route path="/">
					<Layout>
						<LaunchRedirect />
						<MainRoutes />
					</Layout>
				</Route>
			</Switch>
		</HelpdeskContainer>
	);
}
